/**
 * Site Databases Schema
 *
 * Source of truth for site database definitions — field types, access modes,
 * field definitions, and the full database structure.
 *
 * Powers:
 * - AI generation (LanguageModel.generateObject)
 * - Server-side validation (dispatch worker)
 * - Codegen (generates Zod-based databases.ts for Astro sites)
 * - Dashboard UI (field type picker)
 *
 * @module
 */
import { Schema as S } from "effect"

// ═══════════════════════════════════════════════════════════════════════
// Field Types
// ═══════════════════════════════════════════════════════════════════════

export const FieldType = S.Literals([
  "string",
  "text",
  "email",
  "phone",
  "number",
  "boolean",
  "url",
  "date",
  "datetime",
  "select",
  "multiselect",
  "address",
  "city",
  "state",
  "zip",
  "name",
  "first_name",
  "last_name",
  "company",
  "currency",
])
export type FieldType = typeof FieldType.Type

// ═══════════════════════════════════════════════════════════════════════
// Primitive Types
// ═══════════════════════════════════════════════════════════════════════

export const Primitive = S.Literals(["string", "number", "boolean"])
export type Primitive = typeof Primitive.Type

// ═══════════════════════════════════════════════════════════════════════
// Access Modes
// ═══════════════════════════════════════════════════════════════════════

export const AccessMode = S.Literals(["read", "write"])
export type AccessMode = typeof AccessMode.Type

// ═══════════════════════════════════════════════════════════════════════
// Field Type Metadata — rich descriptor for UI + AI context
// ═══════════════════════════════════════════════════════════════════════

export const FieldTypeMetadata = S.Struct({
  kind: FieldType,
  label: S.String,
  description: S.String,
  primitive: Primitive,
  inputType: S.String,
  placeholder: S.String,
  maxLength: S.optional(S.Number),
  parametric: S.optional(S.Boolean),
})
export type FieldTypeMetadata = typeof FieldTypeMetadata.Type

/**
 * Complete metadata for every field type.
 *
 * This array is typed against `FieldTypeMetadata` so adding a new entry
 * to `FieldType` without adding metadata here is a compile error
 * (enforced by the `satisfies` + exhaustiveness helper below).
 */
export const fieldTypeMetadata = [
  {
    kind: "string",
    label: "Short Text",
    description: "A short text value, up to 500 characters",
    primitive: "string",
    inputType: "text",
    placeholder: "Enter text...",
    maxLength: 500,
  },
  {
    kind: "text",
    label: "Long Text",
    description: "A longer text value, up to 5000 characters",
    primitive: "string",
    inputType: "textarea",
    placeholder: "Enter details...",
    maxLength: 5000,
  },
  {
    kind: "email",
    label: "Email",
    description: "A valid email address",
    primitive: "string",
    inputType: "email",
    placeholder: "jane@example.com",
    maxLength: 320,
  },
  {
    kind: "phone",
    label: "Phone Number",
    description: "A phone number with optional formatting",
    primitive: "string",
    inputType: "tel",
    placeholder: "(555) 123-4567",
    maxLength: 30,
  },
  {
    kind: "number",
    label: "Number",
    description: "A finite numeric value",
    primitive: "number",
    inputType: "number",
    placeholder: "0",
  },
  {
    kind: "boolean",
    label: "Yes / No",
    description: "A true or false value",
    primitive: "boolean",
    inputType: "checkbox",
    placeholder: "",
  },
  {
    kind: "url",
    label: "URL",
    description: "A valid web address",
    primitive: "string",
    inputType: "url",
    placeholder: "https://example.com",
    maxLength: 2000,
  },
  {
    kind: "date",
    label: "Date",
    description: "An ISO 8601 date (YYYY-MM-DD)",
    primitive: "string",
    inputType: "date",
    placeholder: "2026-01-15",
  },
  {
    kind: "datetime",
    label: "Date & Time",
    description: "An ISO 8601 datetime",
    primitive: "string",
    inputType: "datetime-local",
    placeholder: "2026-01-15T09:00",
  },
  {
    kind: "select",
    label: "Single Select",
    description: "One choice from a predefined list of options",
    primitive: "string",
    inputType: "select",
    placeholder: "Choose one...",
    parametric: true,
  },
  {
    kind: "multiselect",
    label: "Multi Select",
    description: "One or more choices from a predefined list of options",
    primitive: "string",
    inputType: "select",
    placeholder: "Choose...",
    parametric: true,
  },
  {
    kind: "address",
    label: "Street Address",
    description: "A street address line",
    primitive: "string",
    inputType: "text",
    placeholder: "123 Main St",
    maxLength: 500,
  },
  {
    kind: "city",
    label: "City",
    description: "A city name",
    primitive: "string",
    inputType: "text",
    placeholder: "Austin",
    maxLength: 200,
  },
  {
    kind: "state",
    label: "State",
    description: "A US state name or abbreviation",
    primitive: "string",
    inputType: "text",
    placeholder: "TX",
    maxLength: 100,
  },
  {
    kind: "zip",
    label: "ZIP Code",
    description: "A US ZIP or ZIP+4 code",
    primitive: "string",
    inputType: "text",
    placeholder: "78701",
    maxLength: 10,
  },
  {
    kind: "name",
    label: "Full Name",
    description: "A person's full name",
    primitive: "string",
    inputType: "text",
    placeholder: "Jane Doe",
    maxLength: 300,
  },
  {
    kind: "first_name",
    label: "First Name",
    description: "A person's first name",
    primitive: "string",
    inputType: "text",
    placeholder: "Jane",
    maxLength: 150,
  },
  {
    kind: "last_name",
    label: "Last Name",
    description: "A person's last name",
    primitive: "string",
    inputType: "text",
    placeholder: "Doe",
    maxLength: 150,
  },
  {
    kind: "company",
    label: "Company Name",
    description: "A business or organization name",
    primitive: "string",
    inputType: "text",
    placeholder: "Acme Inc.",
    maxLength: 300,
  },
  {
    kind: "currency",
    label: "Currency Amount",
    description: "A dollar amount (two decimal places)",
    primitive: "number",
    inputType: "number",
    placeholder: "99.99",
  },
] as const satisfies readonly FieldTypeMetadata[]

// Compile-time exhaustiveness check: every FieldType must have metadata
type _AssertAllFieldTypesHaveMetadata = {
  [K in FieldType]: Extract<(typeof fieldTypeMetadata)[number], { readonly kind: K }>
} extends Record<FieldType, infer _> ? true : never
const _exhaustive: _AssertAllFieldTypesHaveMetadata = true
void _exhaustive

// ═══════════════════════════════════════════════════════════════════════
// Field Definition — a single field in a database schema
// ═══════════════════════════════════════════════════════════════════════

export const FieldDef = S.Struct({
  type: FieldType,
  required: S.Boolean,
  /** Present when type is "select" or "multiselect". */
  options: S.optional(S.Array(S.String)),
})
export type FieldDef = typeof FieldDef.Type

// ═══════════════════════════════════════════════════════════════════════
// Database Schema — the fields that make up a database
// ═══════════════════════════════════════════════════════════════════════

export const DatabaseSchema = S.Struct({
  fields: S.Record(S.String, FieldDef),
})
export type DatabaseSchema = typeof DatabaseSchema.Type

// ═══════════════════════════════════════════════════════════════════════
// Site Database — the full definition stored in the DB
// ═══════════════════════════════════════════════════════════════════════

export const SiteDatabase = S.Struct({
  name: S.String,
  description: S.String,
  accessMode: AccessMode,
  schema: DatabaseSchema,
  schemaVersion: S.Number,
})
export type SiteDatabase = typeof SiteDatabase.Type

// ═══════════════════════════════════════════════════════════════════════
// AI Generation Result — output schema for LanguageModel.generateObject
// ═══════════════════════════════════════════════════════════════════════

export const GeneratedFieldDef = S.Struct({
  name: S.String,
  type: FieldType,
  required: S.Boolean,
  options: S.optional(S.Array(S.String)),
  description: S.NullOr(S.String),
})
export type GeneratedFieldDef = typeof GeneratedFieldDef.Type

export const GeneratedDatabase = S.Struct({
  name: S.String,
  description: S.String,
  accessMode: AccessMode,
  fields: S.Array(GeneratedFieldDef),
})
export type GeneratedDatabase = typeof GeneratedDatabase.Type

export const DatabaseGenerationResult = S.Struct({
  databases: S.Array(GeneratedDatabase),
})
export type DatabaseGenerationResult = typeof DatabaseGenerationResult.Type

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

/** Get metadata for a field type by kind. */
export function getFieldTypeMetadata(kind: FieldType): FieldTypeMetadata {
  return fieldTypeMetadata.find((m) => m.kind === kind)!
}

/** All field type kinds that require an `options` parameter. */
export const parametricFieldTypes: readonly FieldType[] = fieldTypeMetadata
  .filter((m) => "parametric" in m && m.parametric === true)
  .map((m) => m.kind as FieldType)
